import React, { useState } from 'react';
import { ChevronLeft, Camera, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/Button';
interface StartRentalProps {
  tripId: string;
  onBack: () => void;
  onComplete: () => void;
}
export function StartRental({
  onBack,
  onComplete
}: StartRentalProps) {
  const [photos, setPhotos] = useState<string[]>([]);
  const [condition, setCondition] = useState('');
  const maxPhotos = 5;
  const handleAddPhoto = () => {
    if (photos.length < maxPhotos) {
      // Simulate photo capture with varied placeholder images
      const placeholders = ['https://images.unsplash.com/photo-1504148455328-c376907d081c?w=400&q=80', 'https://images.unsplash.com/photo-1572981779307-38b8cabb2407?w=400&q=80', 'https://images.unsplash.com/photo-1530124566582-a45a7e3e29f0?w=400&q=80', 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=400&q=80', 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=400&q=80'];
      const nextPhoto = placeholders[photos.length % placeholders.length];
      setPhotos([...photos, nextPhoto]);
    }
  };
  const handleRemovePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };
  const canSubmit = photos.length > 0;
  return <div className="min-h-screen bg-cream pb-24" data-id="element-1547">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-sand" data-id="element-1548">
        <div className="max-w-md mx-auto px-3 py-2 flex items-center" data-id="element-1549">
          <button onClick={onBack} className="p-1.5 -ml-1 hover:bg-sand/20 rounded-full transition-colors" data-id="element-1550">
            <ChevronLeft size={22} className="text-charcoal" data-id="element-1551" />
          </button>
          <h1 className="flex-1 text-base font-bold text-charcoal text-center pr-8" data-id="element-1552">
            Start Booking
          </h1>
        </div>
      </div>

      <div className="max-w-md mx-auto" data-id="element-1553">
        {/* Instruction Text */}
        <div className="px-3 pt-3" data-id="element-1554">
          <p className="text-xs text-charcoal/80 leading-relaxed" data-id="element-1555">
            The owner has requested photos at the start of your booking. This
            helps protect both of you in case of damage or disputes.
          </p>
        </div>

        {/* Item Photos Section */}
        <div className="px-3 mt-3" data-id="element-1556">
          <label className="block text-xs font-bold text-charcoal mb-2" data-id="element-1557">
            Item Photos
          </label>

          <div className="flex flex-wrap gap-2" data-id="element-1558">
            {/* Existing Photos */}
            <AnimatePresence data-id="element-1559">
              {photos.map((photo, index) => <motion.div key={`${photo}-${index}`} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.8
            }} className="relative w-20 h-20 rounded-md overflow-hidden" data-id="element-1560">
                  <img src={photo} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" data-id="element-1561" />
                  <button onClick={() => handleRemovePhoto(index)} className="absolute top-1 right-1 p-0.5 bg-charcoal/70 rounded-full" data-id="element-1562">
                    <X size={12} className="text-white" data-id="element-1563" />
                  </button>
                </motion.div>)}
            </AnimatePresence>

            {/* Add Photo Box */}
            {photos.length < maxPhotos && <motion.button onClick={handleAddPhoto} whileTap={{
            scale: 0.95
          }} className="w-20 h-20 rounded-md border-2 border-dashed border-moss-gray flex flex-col items-center justify-center gap-1 text-moss-gray hover:border-dark-green hover:text-dark-green transition-colors" data-id="element-1564">
                <Camera size={24} data-id="element-1565" />
                <span className="text-[10px] font-medium" data-id="element-1566">Add Photo</span>
              </motion.button>}
          </div>

          {/* Photo Counter */}
          <p className="text-[10px] text-moss-gray mt-1.5" data-id="element-1567">
            {photos.length} / {maxPhotos} photos
          </p>
        </div>

        {/* Condition Description Section */}
        <div className="px-3 mt-4" data-id="element-1568">
          <label className="block text-xs font-bold text-charcoal mb-2" data-id="element-1569">
            Describe the item's condition at pickup
          </label>
          <textarea value={condition} onChange={e => setCondition(e.target.value)} placeholder="Include anything you notice about wear, damage, or functionality. Be as specific as possible." className="w-full h-[120px] p-2.5 border border-sand rounded text-xs text-charcoal placeholder-moss-gray leading-relaxed focus:outline-none focus:ring-1 focus:ring-dark-green focus:border-dark-green resize-y" data-id="element-1570" />
        </div>

        {/* Submit Button */}
        <div className="px-3 mt-3 mb-16" data-id="element-1571">
          <Button variant="primary" fullWidth disabled={!canSubmit} onClick={onComplete} data-id="element-1572">
            Start My Booking
          </Button>
        </div>
      </div>
    </div>;
}